import { brand, system } from '@workday/canvas-tokens-web'
import _ from 'lodash'
import omit from 'lodash/omit'
import { isValidElement, useCallback, useMemo, useRef } from 'react'
import Select, { components } from 'react-select'
import CreatableSelect from 'react-select/creatable'
import { forceArray, frozenArr } from '~/common/arrayUtils'
import { asFormikField } from '~/common/formikUtils'
import { isClient } from '~/common/nextJsUtils'
import { isOptionShape } from '~/common/objectUtils'
import { TOP_NAV_HEIGHT } from '~/common/scrollUtils'
import { Box, useFormControlContext, useTheme } from '~/design_system'
import { Msg, useMsg } from '~/common/localizationUtils'
import { useFormControlExtendedContext } from '~/design_system/components/form_controls/FormControlExtendedContext'
import { mergeRefs } from '~/common/reactUtils'
import { resolveTextStyle } from '~/theme/components/ReactSelect'

export const vndlySelectTheme = theme => ({
  ...theme,
  borderRadius: 2,
  colors: {
    ...theme.colors,
    primary: 'var(--cnvs-brand-primary-base)',
    primary25: '#F5F5F5',
    neutral80: '#59636F'
  }
})

const isNonValue = val => val === null || val === undefined || val === ''

const getInactiveStyledLabel = (data, isSelected) =>
  // Selected option or singleValue that have an $inactive property set to `true`
  // will look grayed out and have extra labeling e.g. 'Some Option (Inactive)'
  // which can be customized using $inactive_label, e.g. 'Some Option (Deleted)'
  data?.$inactive && {
    ...(!isSelected && { color: '#a0aab6' }),
    '& div:after': { content: `' ${data?.$inactive_label ?? '(Inactive)'}'` }
  }

function focusRing(color = 'transparent') {
  // If the color is transparent, we don't want to show the white ring
  const innerRing = `0px 0px 0px 2px ${color === 'transparent' ? 'transparent' : 'white'}`
  const outerRing = `0px 0px 0px 4px ${color}`
  return `${innerRing}, ${outerRing}`
}

export const vndlySelectStyles = theme => {
  // Rebrand marker set on workdayThemeSanaRebrand. Flag-OFF it's absent, so every
  // Canvas-specific rule below is skipped and VndlySelect renders byte-identical to its
  // legacy self. Mirrors useSelectStyle (theme/components/ReactSelect.ts) so this legacy
  // select and the new ReactSelect read as the same field flag-ON.
  const isRebrand = Boolean(theme?.isSanaRebrand)
  const radii = theme?.radii ?? {}
  const fieldRadius = `var(${system.shape.md})` // flag-ON 8px; flag-OFF polyfilled to legacy 0.25rem
  const menuRadius = radii.x2
  const optionRadius = '20px' // Simulates a fully rounded pill for single line (which are 40px tall), but multiline looks like a rounded corners
  const bodyType = isRebrand ? resolveTextStyle(theme, 'body') : {}

  // Colors point at the non-deprecated Canvas system tokens the migrated Input/ReactSelect
  // use. These need no flag gate: the legacy middleman vars VndlySelect used pre-migration
  // already resolved to these same tokens flag-OFF (legacyWorkdayTheme Layer 1), so dropping
  // the ternary keeps flag-OFF byte-identical while removing the middleman vars.
  const borderDefault = `var(${system.color.border.input.default})`
  const borderHover = `var(${system.color.border.input.hover})`
  const borderInvalid = `var(${system.color.brand.border.critical})`
  const borderWarned = `var(${brand.common.caution.inner})`
  const textColor = `var(${system.color.fg.default})`
  const textDisabled = `var(${system.color.fg.disabled})`
  const placeholderColor = `var(${system.color.fg.muted.default})`
  // "No value" option/value text — flag-OFF keeps the legacy hardcoded gray (no semantic
  // token resolves to it); flag-ON rides the muted fg token.
  const nonValueColor = isRebrand ? `var(${system.color.fg.muted.default})` : '#a0aab6'
  // Focus ring — the Canvas focus-outline token direct (the value the legacy
  // --vndly-color-input-*-focused vars resolved to in both flag states).
  const focusColor = `var(${brand.common.focusOutline})`
  // Disabled/readonly surface — the semantic token the legacy
  // --vndly-color-input-background-disabled var already resolved to (both flags).
  const disabledBg = `var(${system.color.bg.alt.softer})`

  return {
    option: (provided, { data, type, value, isSelected, isFocused }) => ({
      ...provided,
      ...(type === 'option' && isNonValue(value) && !isSelected && { color: nonValueColor }),
      ...(type === 'option' && getInactiveStyledLabel(data, isSelected)),
      fontWeight: '400',
      fontSize: '14px',
      minHeight: '2.375rem',
      ...(isRebrand && {
        // Canvas shape.round pill on hover/select, with a small vertical gap and the Canvas
        // type ramp — same treatment as the new ReactSelect menu. The focused/selected
        // backgrounds are set here (Canvas surface + brand tokens) so flag-ON no longer
        // relies on vndlySelectTheme's legacy primary25/primary colors.
        ...bodyType,
        borderRadius: optionRadius,
        paddingInline: '1rem',
        marginBlock: '2px',
        backgroundColor: 'transparent',
        '&:active': { backgroundColor: `var(${system.color.bg.alt.strong})` },
        ...(type === 'option' &&
          isFocused &&
          !isSelected && { backgroundColor: `var(${system.color.bg.alt.default})` }),
        ...(type === 'option' && isSelected && { color: 'white', backgroundColor: `var(${brand.primary.base})` })
      })
    }),
    singleValue: (provided, { data, selectProps: { readOnly } }) => ({
      ...provided,
      color: isNonValue(data?.value) ? nonValueColor : textColor,
      ...getInactiveStyledLabel(data),
      fontWeight: 'var(--vndly-font-weight-normal)',
      fontSize: 'var(--vndly-font-size-md)',
      ...(isRebrand && bodyType),
      '[data-react-select]:has(input[disabled]) &': {
        color: textDisabled
      },
      ...(readOnly && { userSelect: 'text' })
    }),
    // Master had no `input` override; flag-ON needs an explicit value color because the
    // control's spread type ramp (bodyType) carries none. Flag-OFF this is a no-op.
    input: provided => ({
      ...provided,
      ...(isRebrand && { color: `var(${system.color.fg.default})` })
    }),
    control: (provided, { isFocused, isDisabled, selectProps: { readOnly } }) => ({
      ...provided,
      ...(isRebrand && bodyType),
      '.has-error &, [data-invalid] ~ [data-qa="select"] &, [data-react-select][data-invalid="true"] &': {
        borderWidth: '2px',
        borderColor: borderInvalid,
        ...(isFocused && { boxShadow: focusRing(focusColor) })
      },
      '[data-warned] ~ [data-qa="select"] &, [data-react-select][data-warned="true"] &': {
        borderWidth: '2px',
        borderColor: borderWarned,
        ...(isFocused && { boxShadow: focusRing(focusColor) })
      },
      ...(!isFocused &&
        !readOnly &&
        !isDisabled && {
          '&:hover': {
            borderColor: borderHover
          }
        }),
      borderRadius: fieldRadius,
      borderColor: borderDefault,
      ...(isFocused && {
        borderColor: focusColor,
        boxShadow: `0 0 0 1px ${focusColor}`,
        '&:hover': {
          borderColor: focusColor
        }
      }),
      ...(readOnly && {
        cursor: 'default',
        backgroundColor: disabledBg,
        ...(!isFocused && {
          '&:hover': {
            borderColor: borderDefault
          }
        })
      }),
      ...(isDisabled && {
        cursor: 'not-allowed',
        color: textDisabled,
        backgroundColor: disabledBg,
        ...(isRebrand && { borderColor: `var(${system.color.fg.disabled})` })
      })
    }),
    valueContainer: (provided, { selectProps: { readOnly, isMulti } }) => ({
      ...provided,
      // a text highlight cursor
      ...(readOnly && { cursor: 'text' }),
      ...(isMulti && { padding: '0 8px' }),
      minHeight: '2.375rem'
    }),
    menu: provided => ({
      ...provided,
      borderRadius: isRebrand ? menuRadius : '0.25rem',
      zIndex: 10
    }),
    menuList: provided => ({
      ...provided,
      // Inset the pill options from the rounded menu edges (flag-ON only).
      ...(isRebrand && { paddingInline: '0.25rem' })
    }),
    menuPortal: provided => ({
      ...provided,
      zIndex: 1500
    }),
    indicatorSeparator: () => ({
      display: 'none'
    }),
    dropdownIndicator: (provided, { selectProps: { readOnly }, isDisabled }) => ({
      ...provided,
      ...((readOnly || isDisabled) && { display: 'none' }),
      ...(isRebrand && {
        // Caret rides the value/text color (Canvas fg.default = oklch(0 0 0 / 0.77)).
        color: `var(${system.color.fg.default})`,
        '&:hover': { color: `var(${system.color.fg.default})` }
      }),
      padding: '0 6px',
      transform: 'scale(.75)'
    }),
    clearIndicator: (provided, { selectProps: { readOnly }, isDisabled }) => ({
      ...provided,
      ...((readOnly || isDisabled) && { display: 'none' }),
      padding: '8px 3px',
      position: 'relative'
    }),
    placeholder: (provided, { selectProps: { readOnly }, isDisabled }) => ({
      ...provided,
      color: placeholderColor,
      ...(readOnly && { display: 'none' }),
      ...(isDisabled && { color: placeholderColor })
    }),
    multiValue: (provided, { isDisabled }) => ({
      ...provided,
      borderRadius: 4,
      ...(isDisabled && { backgroundColor: 'white' })
    }),
    multiValueLabel: (provided, { data, selectProps: { readOnly }, isDisabled }) => ({
      ...provided,
      color: textColor,
      ...(data?.$locked && { paddingRight: '6px' }),
      ...((readOnly || isDisabled) && { paddingRight: '6px' }),
      ...(isDisabled && { color: textDisabled })
    }),
    multiValueRemove: (provided, { data, selectProps: { readOnly }, isDisabled }) => {
      return {
        ...provided,
        ...((readOnly || isDisabled) && { display: 'none' }),
        ...(data?.$locked && { display: 'none' })
      }
    }
  }
}

export const buildVndlySelectStyles = (props = {}, theme) => {
  const { style = {}, styles = {} } = props

  // If a `style` prop is provided, use it directly (for backward compatibility)
  if (!_.isEmpty(style)) {
    console.warn('[VndlySelect] The `style` prop is deprecated. Please use the `styles` prop to customize styles.')
  }

  const containerStyle = { container: typeof style === 'function' ? style() : style }
  const merged = vndlySelectStyles(theme)
  const allKeys = Array.from(new Set([...Object.keys(merged), ...Object.keys(styles), 'container']))

  // Otherwise, merge the provided `styles` overrides with the default styles
  allKeys.forEach(key => {
    const base = merged[key]
    const custom = styles[key]
    const container = containerStyle[key]
    merged[key] = (provided, state) => {
      const baseResolved = typeof base === 'function' ? base(provided, state) : base
      const customResolved = typeof custom === 'function' ? custom(baseResolved, state) : custom
      return {
        ...provided,
        ...baseResolved,
        ...customResolved,
        ...container
      }
    }
  })

  return merged
}

/**
 * Extend react-select's SelectContainer to add title to the div in order to render a tooltip
 */
const SelectContainer = ({ getValue, innerProps, ...props }) => {
  let value = getValue()
  // Even for non-multi selects, we get an array here.
  if (Array.isArray(value) && value.length > 0) {
    value = value[0]
  }
  const title = isOptionShape(value) ? value.label : value
  return <components.SelectContainer {...props} innerProps={{ ...innerProps, title }} />
}

const MultiValueLabel = ({ innerProps, data, ...props }) => {
  const title = data.label
  return (
    <components.MultiValueLabel {...props} data={data} innerProps={{ ...innerProps, title }}>
      <CustomLabel {...data} />
    </components.MultiValueLabel>
  )
}

// Support to wrap label in a custom component
const CustomLabel = ({ label = {}, subLabel = '', LabelContainer, isSelected }) => {
  const subLabelStyle = {
    fontSize: '13px',
    color: isSelected ? 'white' : 'var(--vndly-color-input-text-hint)',
    whiteSpace: 'normal'
  }

  const labelDisplay = (
    <>
      {/* For some reason in the React 18 upgrade label can sometimes be {} */}
      <Box data-select-option-label whiteSpace="normal">
        {Object.keys(label).length === 0 ? '' : label}
      </Box>
      {subLabel && (
        <div data-select-option-sublabel style={subLabelStyle}>
          {subLabel}
        </div>
      )}
    </>
  )

  if (LabelContainer) {
    return <LabelContainer>{labelDisplay}</LabelContainer>
  }
  return <>{labelDisplay}</>
}

// Add support to render icons along with the selected Option
const SingleValue = ({ data, ...props }) => (
  <components.SingleValue data={data} {...props}>
    <CustomLabel {...data} />
  </components.SingleValue>
)

// Add support to render icons along with Options
const Option = ({ data, ...props }) => {
  return (
    <components.Option data={data} {...props}>
      <CustomLabel {...data} isSelected={props.isSelected} />
    </components.Option>
  )
}

const MenuList = ({ children, ...props }) => (
  <div data-react-select-menu-list>
    {props.selectProps.menuListMessage ? (
      <components.MenuList {...omit(props, ['id'])} innerProps={omit(props.innerProps, ['id'])}>
        {props.selectProps.menuListMessage}
      </components.MenuList>
    ) : null}
    <components.MenuList {...props}>{children}</components.MenuList>
  </div>
)

const Input = props => {
  // aria-label and aria-labelledby are already spread by the existing props if provided
  const ariaProps = {
    'aria-describedby': [props['aria-describedby'], props.selectProps['aria-describedby']].filter(Boolean).join(' '),
    'aria-required': props.selectProps.isRequired || undefined
  }

  return <components.Input data-react-select-input readOnly={props.selectProps.readOnly} {...props} {...ariaProps} />
}

const CustomClear = ({ ...props }) => {
  const msg = useMsg()
  return (
    <button
      type="button"
      onClick={() => {
        props.clearValue()
      }}
      aria-label={msg('invoice_billing.clear_selections')}>
      <components.ClearIndicator {...props} />
    </button>
  )
}

// Custom Control to allow text selection in read-only mode
// React-select's default onMouseDown calls preventDefault() which blocks text selection
const Control = ({ innerProps, selectProps, ...props }) => {
  if (!selectProps.readOnly) {
    return <components.Control {...props} innerProps={innerProps} selectProps={selectProps} />
  }
  // If readOnly remove any mouse events that would prevent users from selecting and copying the value text
  const modifiedInnerProps = {
    ...innerProps,
    onMouseDown: undefined,
    onTouchEnd: undefined
  }
  return <components.Control {...props} innerProps={modifiedInnerProps} selectProps={selectProps} />
}

const singleComponents = {
  SelectContainer,
  SingleValue,
  Option,
  MenuList,
  Input,
  Control,
  ClearIndicator: CustomClear
}
const multiComponents = {
  MultiValueLabel,
  Option,
  Input,
  Control,
  ClearIndicator: CustomClear
}

export const mapSimpleStringOptions = (options, emptyLabel, msg = null) => {
  return Array.isArray(options)
    ? options.map(option => {
        const shaped = isOptionShape(option) ? { ...option } : { label: option, value: option }
        if (!shaped.label && !shaped.value) shaped.label = emptyLabel
        // Handle calling `msg()` on the label if it was created with <Msg /> or lazyMsg()
        if (msg && isValidElement(shaped.label) && shaped.label.type === Msg) {
          shaped.label = msg(shaped.label)
        }
        return shaped
      })
    : options
}

function optionizeValue(val, { creatable } = {}) {
  if (isOptionShape(val)) {
    if (creatable && val.label === '') return { label: val.value, value: val.value }
    return val
  }
  return { label: val, value: val }
}

export function getOptionsThatMatchValue(value, options, { creatable } = {}) {
  // eslint-disable-next-line no-prototype-builtins
  const optionsAreInGroups = options[0]?.hasOwnProperty('label') && options[0]?.hasOwnProperty('options')
  const newOptions = optionsAreInGroups ? options.flatMap(group => group.options) : options
  // Loop through the selected value(s)
  return forceArray(value).map(val => {
    // If we can find a match in the options, map to it
    const matchingOption = newOptions.find(option => option?.value === (val?.value ?? val))
    if (matchingOption != null) return matchingOption

    // Otherwise "optionize" the value if necessary
    return optionizeValue(val, { creatable })
  })
}

export function VndlySelectInternal({
  creatable,
  disabled,
  options = frozenArr,
  value = null,
  isMulti = false,
  readOnly,
  id: idProp = null,
  emptyLabel = 'No Selection',
  onChange = () => {},
  onMouseEnter = _.noop,
  menuPortal = true,
  placeholder,
  name = 'select',
  onBlur = () => {},
  isSearchable = true,
  style = {},
  styles = {},
  testId = 'select',
  ref: outerRef,
  ...props
}) {
  const innerRef = useRef(null)
  const ref = mergeRefs(outerRef, innerRef)
  const otherProps = {
    name,
    onBlur,
    isSearchable,
    style,
    styles,
    testId,
    ...props
  }
  const msg = useMsg()
  const selectOptions = useMemo(() => mapSimpleStringOptions(options, emptyLabel, msg), [options, emptyLabel, msg])
  const selectValue = useMemo(() => {
    if (value === null) return value
    const matchingOpts = getOptionsThatMatchValue(value, selectOptions, { creatable })
    return isMulti ? matchingOpts : matchingOpts[0]
  }, [value, selectOptions, creatable, isMulti])
  const getOptionLabelSelect = option => `${option.label}${option.subLabel ? ` ${option.subLabel}` : ''}`
  const internalOnChange = (values, actionObj) => {
    let optionValues = values
    switch (actionObj.action) {
      case 'remove-value':
      case 'pop-value':
        if (actionObj.removedValue?.$locked) {
          return
        }
        break
      case 'clear':
        optionValues = options.filter(v => v.$locked)
        // when we clear the select, focus back on it
        innerRef.current?.focus()
        break
      default:
        break
    }
    onChange(optionValues, actionObj)
  }

  const {
    isDisabled,
    isReadOnly,
    isRequired,
    isInvalid,
    id: formControlId,
    hasHelpText,
    helpTextId
  } = useFormControlContext() ?? {}

  const { isWarned } = useFormControlExtendedContext() ?? {}
  const theme = useTheme()

  const id = idProp ?? formControlId
  const closeMenuProps = useCloseMenuIfItGoesBehindAnything(innerRef, otherProps)
  const localeNoOptionsMessage = () => {
    return msg('common.ui.no_options')
  }
  const resolvedIsRequired = isRequired || otherProps.isRequired || otherProps.required
  const resolvedIsSearchable = readOnly || isReadOnly ? false : isSearchable
  const hasValue = Array.isArray(selectValue) ? selectValue.some(v => !!v) : selectValue != null
  // react-select renders DummyInput instead of Input when not searchable (e.g. readOnly), and
  // DummyInput only picks up aria-required from react-select's own required prop. Passing it is
  // safe here: the hidden RequiredInput only renders when the select is empty, which hasValue rules out.
  const dummyInputNeedsRequired = resolvedIsRequired && !resolvedIsSearchable && hasValue
  const selectProps = {
    placeholder: placeholder === undefined ? msg('common.ui.select_placeholder') : placeholder,
    noOptionsMessage: localeNoOptionsMessage,
    menuPlacement: 'auto',
    menuPosition: 'fixed',
    isMulti,
    ...(isInvalid && { 'aria-invalid': isInvalid }),
    'aria-errormessage': `${id}-feedback`,
    'aria-describedby': hasHelpText ? helpTextId : '',
    ...(menuPortal && isClient() && { menuPortalTarget: document.body }),
    ...otherProps,
    options: selectOptions,
    value: selectValue,
    theme: vndlySelectTheme,
    styles: buildVndlySelectStyles(otherProps, theme),
    isDisabled: disabled || isDisabled,
    readOnly: readOnly || isReadOnly,
    isRequired: resolvedIsRequired,
    required: otherProps.required || dummyInputNeedsRequired || undefined,
    isSearchable: resolvedIsSearchable,
    inputId: id,
    instanceId: id,
    components: isMulti ? multiComponents : singleComponents,
    getOptionLabel: getOptionLabelSelect,
    onChange: internalOnChange,
    isClearable:
      isMulti && Array.isArray(value)
        ? value?.some(
            v =>
              !_.get(
                options?.find(opt => opt?.value === v?.value),
                '$locked',
                false
              )
          )
        : _.get(otherProps, 'isClearable', false),
    menuIsOpen: readOnly || isReadOnly ? false : undefined,
    ...closeMenuProps
  }

  return (
    <div
      data-react-select
      data-qa={selectProps.testId}
      data-testid={selectProps.testId}
      onMouseEnter={onMouseEnter}
      data-warned={isWarned}
      data-invalid={isInvalid}>
      {creatable ? <CreatableSelect ref={ref} {...selectProps} /> : <Select ref={ref} {...selectProps} />}
    </div>
  )
}

VndlySelectInternal.displayName = 'VndlySelectInternal'

function useCloseMenuIfItGoesBehindAnything(ref, props) {
  const isMenuOpenRef = useRef(false)
  const isMenuClippedByControlScrollParent = control => {
    let el = control.parentElement
    const menuRect = control.getBoundingClientRect()
    while (el && el !== document.body) {
      const style = window.getComputedStyle(el)
      const overflowY = style.overflowY
      const overflowX = style.overflowX
      const hasScroll = /(auto|scroll|hidden)/.test(overflowY + overflowX)
      if (hasScroll) {
        const containerRect = el.getBoundingClientRect()
        const clipped =
          menuRect.bottom > containerRect.bottom ||
          menuRect.top < containerRect.top ||
          menuRect.left < containerRect.left ||
          menuRect.right > containerRect.right
        if (clipped) return true
      }
      el = el.parentElement
    }
    return false
  }
  return {
    closeMenuOnScroll: event => {
      const select = ref.current.inputRef
      const menuList = ref.current.menuListRef
      const control = ref.current.controlRef
      if (props.closeMenuOnScroll && !select && !menuList) {
        return true
      }
      if (!isMenuOpenRef.current) {
        return false
      }
      if (!menuList) {
        return false
      }
      // Don't close menu if scrolling within the menu list itself
      if (event?.target && menuList.contains(event.target)) {
        return false
      }
      const top = menuList.getBoundingClientRect().top
      if (top < TOP_NAV_HEIGHT) {
        return true
      }
      return isMenuClippedByControlScrollParent(control)
    },
    onMenuOpen: useCallback(
      (...args) => {
        isMenuOpenRef.current = true
        return props.onMenuOpen?.(...args)
      },
      [props]
    ),
    onMenuClose: useCallback(
      (...args) => {
        isMenuOpenRef.current = false
        return props.onMenuClose?.(...args)
      },
      [props]
    )
  }
}
/*
  TODO: add `autoComplete` prop when it is configurable [PR: https://github.com/JedWatson/react-select/pull/4105]
  This prop is required to properly disable chrome's autofill being shown in Chrome ver. 86+ and few possible solutions(?) are listed in thread
  https://bugs.chromium.org/p/chromium/issues/detail?id=914451&can=2&q=Autofill%20&colspec=ID%20Pri%20M%20Stars%20ReleaseBlock%20Component%20Status%20Owner%20Summary%20OS%20Modified
*/

// When used in a Formik Form, VndlySelect behaves like a normal select. It only cares
// about the 'value' of the option, not the whole option object. This goes for both
// value and onChange.
export const formikPropMapper = ({
  field,
  form,
  options: _options,
  isMulti,
  extendOnChange = () => {},
  onCreate = null,
  ...rest
}) => ({
  onChange: (option, { action }) => {
    extendOnChange(option, { action })
    if (option === null) {
      form.setFieldValue(field.name, isMulti ? [] : null)
      return
    }
    switch (action) {
      case 'select-option':
      case 'remove-value':
        form.setFieldValue(field.name, isMulti ? option.map(o => o.value) : option.value)
        break
      case 'pop-value':
        break
      case 'clear':
        form.setFieldValue(field.name, null)
        break
      case 'create-option':
        if (typeof onCreate === 'function') {
          onCreate(isMulti ? option.map(o => o.value) : option.value)
        } else {
          form.setFieldValue(field.name, isMulti ? option.map(o => o.value) : option.value)
        }
        break
      default:
        break
    }
  },
  ...rest
})

/**
 * @agent-transitional prefer ReactSelect from ~/design_system for single select; VndlySelect remains for multi-select until ReactSelect supports it
 */
const VndlySelect = asFormikField(VndlySelectInternal, formikPropMapper)
VndlySelect.displayName = 'VndlySelect'
export default VndlySelect
